import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Truck } from 'lucide-react';
import { contactInfo, mockCoverageAreas } from '../mock';
import { openCookiePreferences } from './CookieBanner';

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      // If not on home page, navigate to home first, then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleAboutClick = () => {
    navigate('/about');
  };

  const handleReviewsClick = () => {
    navigate('/reviews');
  };

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.1),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(6,182,212,0.08),transparent_50%)]" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold text-lg">
                  AIS
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-bold">AIS Express</span>
                  <span className="text-xs text-emerald-400 font-medium">Same Day Delivery</span>
                </div>
              </div>
              <p className="text-slate-300 mb-6 leading-relaxed">
                Your trusted partner for reliable same-day delivery across the UK. 
                Fast, secure, and professional service for businesses of all sizes.
              </p>
              
              {/* Social Media Links */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-white mb-3">Follow Us</h4>
                <div className="flex space-x-4">
                  <a
                    href="https://www.facebook.com/profile.php?id=61578538330304"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center h-10 w-10 rounded-full bg-slate-800 hover:bg-blue-600 transition-colors duration-200"
                    aria-label="Facebook"
                  >
                    <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  
                  <a
                    href="https://www.instagram.com/ais.xpress/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center h-10 w-10 rounded-full bg-slate-800 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
                    aria-label="Instagram"
                  >
                    <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"/>
                    </svg>
                  </a>
                  
                  <a
                    href="https://www.tiktok.com/@ais.express"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center h-10 w-10 rounded-full bg-slate-800 hover:bg-black transition-colors duration-200"
                    aria-label="TikTok"
                  >
                    <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                  </a>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 text-emerald-400">
                <Truck className="h-5 w-5" />
                <span className="font-medium">15,000+ Successful Deliveries</span>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-6 text-white">Quick Links</h3>
              <nav className="space-y-3">
                <button 
                  onClick={() => scrollToSection('home')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Home
                </button>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Our Services
                </button>
                <button 
                  onClick={handleAboutClick}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  About Us
                </button>
                <button 
                  onClick={handleReviewsClick}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Customer Reviews
                </button>
                <button 
                  onClick={() => navigate('/faq')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  FAQ
                </button>
                <button 
                  onClick={() => navigate('/blog')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Blog & News
                </button>
              </nav>
            </div>

            {/* Location Pages */}
            <div>
              <h3 className="text-lg font-semibold mb-6 text-white">Service Areas</h3>
              <nav className="space-y-3">
                <button 
                  onClick={() => navigate('/location/reading')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Reading
                </button>
                <button 
                  onClick={() => navigate('/location/bracknell')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Bracknell
                </button>
                <button 
                  onClick={() => navigate('/location/slough')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Slough
                </button>
                <button 
                  onClick={() => navigate('/location/newbury')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Newbury
                </button>
                <button 
                  onClick={() => navigate('/location/wokingham')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Wokingham
                </button>
                <button 
                  onClick={() => navigate('/location/windsor')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Windsor
                </button>
                <button 
                  onClick={() => navigate('/location/maidenhead')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Maidenhead
                </button>
                <button 
                  onClick={() => navigate('/location/basingstoke')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Basingstoke
                </button>
                <button 
                  onClick={() => navigate('/location/farnborough')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Farnborough
                </button>
                <button 
                  onClick={() => navigate('/location/camberley')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Camberley
                </button>
                <button 
                  onClick={() => navigate('/location/ascot')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Ascot
                </button>
                <button 
                  onClick={() => navigate('/location/woodley')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Woodley
                </button>
                <button 
                  onClick={() => navigate('/location/henley-on-thames')}
                  className="block text-slate-300 hover:text-emerald-400 transition-colors duration-200 text-left"
                >
                  Henley-on-Thames
                </button>
              </nav>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold mb-6 text-white">Contact Info</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Phone className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-slate-300 font-medium">Call for Quote</div>
                    <a 
                      href={`tel:${contactInfo.phone}`}
                      className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 font-semibold"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-slate-300 font-medium">Email Us</div>
                    <a 
                      href={`mailto:${contactInfo.email}`}
                      className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 break-all"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-slate-300 font-medium">Service Area</div>
                    <div className="text-emerald-400">{contactInfo.address}</div>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-slate-300 font-medium">Support Hours</div>
                    <div className="text-emerald-400">{contactInfo.hours}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coverage Areas */}
        <div className="border-t border-slate-800 py-8">
          <h4 className="text-lg font-semibold mb-4 text-white">Major Coverage Areas</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-3">
            {["Reading", "Slough", "Wokingham", "Bracknell", "Windsor", "Maidenhead", "Basingstoke", "Farnborough"].map((area, index) => (
              <div key={index} className="text-slate-400 text-sm bg-slate-800 px-3 py-2 rounded-lg text-center hover:bg-slate-700 transition-colors duration-200">
                {area}
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-sm mt-4">
            Based in Berkshire, we also provide delivery services across the entire United Kingdom
          </p>
          
          {/* Extended Coverage */}
          <div className="mt-6 bg-gradient-to-r from-slate-800 to-slate-900 rounded-lg p-4 border border-slate-700">
            <h5 className="text-emerald-400 font-semibold mb-2 flex items-center">
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Extended Coverage
            </h5>
            <p className="text-slate-300 text-sm leading-relaxed">
              We also serve London, Birmingham, Manchester, and other major UK cities for larger commercial deliveries and specialized transport needs.
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-slate-400 text-sm">
              © 2024 AIS Express. All rights reserved.
            </div>
            <div className="flex items-center space-x-6 text-slate-400 text-sm">
              <button 
                onClick={() => navigate('/privacy')}
                className="hover:text-emerald-400 transition-colors duration-200"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => navigate('/terms')}
                className="hover:text-emerald-400 transition-colors duration-200"
              >
                Terms of Service
              </button>
              <button 
                onClick={() => navigate('/refund')}
                className="hover:text-emerald-400 transition-colors duration-200"
              >
                Refund Policy
              </button>
              <button 
                onClick={() => navigate('/cookies')}
                className="hover:text-emerald-400 transition-colors duration-200"
              >
                Cookie Policy
              </button>
              <button 
                onClick={() => openCookiePreferences()}
                className="hover:text-emerald-400 transition-colors duration-200"
                data-testid="footer-manage-cookies"
              >
                Manage Cookies
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};